import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Alert, CircularProgress, Button, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getMatrixData, saveFeatures, getContacts } from '../api/featureMap';
import MatrixView from '../components/matrix/MatrixView';
import SaveBufferIndicator from '../components/matrix/SaveBufferIndicator';
import ComponentEditDialog from '../components/matrix/ComponentEditDialog';
import ComponentAddDialog from '../components/matrix/ComponentAddDialog';
import FeatureCreateDialog from '../components/matrix/FeatureCreateDialog';

// FEAT-002 매트릭스 뷰 페이지 + FEAT-003 매핑 입력 도구 (Phase 3 v1).
// 결정 1 / 2 (브라우저 직접 조합) / 10 (MatrixPage 가 buffer state 보유) / 12 (beforeunload 가드).
export default function MatrixPage() {
  const [searchParams] = useSearchParams();
  // FEAT-010 — Project 카드 → /matrix?project=<slug> 진입 시 관점 3 자동 + 해당 Project 필터
  const projectParam = searchParams.get('project') || '';
  const [data, setData] = useState({ products: [], features: [], softwares: [], projects: [] });
  const [originalFeatures, setOriginalFeatures] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // FEAT-003 buffer + saveFeatures
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // 모달 state
  const [editTarget, setEditTarget] = useState(null);   // { feature, componentIndex }
  const [addTarget, setAddTarget] = useState(null);     // feature
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getMatrixData(), getContacts()])
      .then(([res, c]) => {
        if (cancelled) return;
        setData(res);
        // 원본 보존 — buffer diff 비교용 (deep clone via JSON)
        setOriginalFeatures(JSON.parse(JSON.stringify(res.features || [])));
        setMembers(c.membersFlat || []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // 변경 카운트 (원본 vs 현재 features diff — id 단위)
  const changeCount = useMemo(() => {
    if (!originalFeatures.length && !data.features.length) return 0;
    const origMap = new Map(originalFeatures.map((f) => [f.id, JSON.stringify(f)]));
    const currMap = new Map(data.features.map((f) => [f.id, JSON.stringify(f)]));
    let count = 0;
    // 변경 + 신규
    for (const [id, json] of currMap) {
      if (origMap.get(id) !== json) count++;
    }
    // 삭제
    for (const id of origMap.keys()) {
      if (!currMap.has(id)) count++;
    }
    return count;
  }, [data.features, originalFeatures]);

  // 결정 12 — beforeunload 가드
  useEffect(() => {
    const h = (e) => {
      if (changeCount > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [changeCount]);

  // 헬퍼: features 상태 갱신
  const updateFeatures = (mutator) => {
    setData((prev) => ({ ...prev, features: mutator(prev.features) }));
  };

  // FEAT-003 핸들러 ---------------------------------------------------------
  const handleComponentEdit = (feature, componentIndex) => {
    setEditTarget({ feature, componentIndex });
  };

  const handleComponentEditSave = (updatedComponent) => {
    if (!editTarget) return;
    const { feature, componentIndex } = editTarget;
    updateFeatures((features) =>
      features.map((f) => {
        if (f.id !== feature.id) return f;
        const next = [...(f.components || [])];
        next[componentIndex] = updatedComponent;
        return { ...f, components: next };
      }),
    );
  };

  const handleAddComponent = (feature) => {
    setAddTarget(feature);
  };

  const handleComponentAddSave = (newComponent) => {
    if (!addTarget) return;
    updateFeatures((features) =>
      features.map((f) => {
        if (f.id !== addTarget.id) return f;
        return { ...f, components: [...(f.components || []), newComponent] };
      }),
    );
  };

  const handleDeleteFeature = (feature) => {
    if (!feature) return;
    if (!window.confirm(`Feature "${feature.id}" 삭제? (저장 전까지 되돌릴 수 있음)`)) return;
    updateFeatures((features) => features.filter((f) => f.id !== feature.id));
  };

  const handleCreateFeatureSave = (newFeature) => {
    updateFeatures((features) => [...features, newFeature]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveMsg('');
    try {
      // 백엔드는 features 전체 배열을 받음 (기존 FeatureMapPage 패턴 동일)
      const result = await saveFeatures(data.features, '매트릭스 뷰에서 매핑 편집');
      const shortSha = (result.commitSha || '').slice(0, 7);
      setSaveMsg(`✅ main 직접 반영 — commit ${shortSha}`);
      // 저장 성공 — 현재 buffer 를 새 원본으로 설정 (diff 0 으로)
      setOriginalFeatures(JSON.parse(JSON.stringify(data.features)));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const existingFeatureIds = useMemo(
    () => new Set(data.features.map((f) => f.id)),
    [data.features],
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h2">매핑 매트릭스 뷰</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          disabled={loading}
        >
          새 Feature
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
        제품 ↔ 소프트웨어 ↔ 기능 ↔ 버전 매핑 현황. 관점 2 의 Badge 를 클릭하면 편집 가능하고,
        행의 ⋮ 메뉴에서 구성 SW 추가 / Feature 삭제가 가능합니다. 변경 사항은 로컬에 누적되고
        "저장" 버튼 클릭 시 main 에 직접 반영됩니다 (~3초).
        <br />
        제품 {data.products.length}건 · 기능 {data.features.length}건 · 소프트웨어 {data.softwares.length}건
      </Typography>

      <SaveBufferIndicator
        changeCount={changeCount}
        saving={saving}
        onSave={handleSave}
      />

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={28} />
          <Typography variant="body2">데이터 불러오는 중...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {saveMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveMsg('')}>
          {saveMsg}
        </Alert>
      )}

      {!loading && !error && (
        <MatrixView
          products={data.products}
          features={data.features}
          softwares={data.softwares}
          projects={data.projects}
          initialVariant={projectParam ? 'project-feature' : 'product-sw'}
          initialProject={projectParam}
          onComponentEdit={handleComponentEdit}
          onAddComponent={handleAddComponent}
          onDeleteFeature={handleDeleteFeature}
        />
      )}

      {/* 모달 */}
      <ComponentEditDialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        component={editTarget ? editTarget.feature.components[editTarget.componentIndex] : null}
        softwares={data.softwares}
        onSave={handleComponentEditSave}
      />
      <ComponentAddDialog
        open={!!addTarget}
        onClose={() => setAddTarget(null)}
        feature={addTarget}
        softwares={data.softwares}
        onSave={handleComponentAddSave}
      />
      <FeatureCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        existingFeatureIds={existingFeatureIds}
        members={members}
        onSave={handleCreateFeatureSave}
      />
    </Box>
  );
}
